import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, CornerDownLeft, ArrowRight } from 'lucide-react';
import { PRODUCTS } from './products';
import type { Product } from './products';
type Props = {
  open: boolean;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
};
// Levenshtein distance — for "did you mean" suggestions
function distance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({
    length: m + 1
  }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1].toLowerCase() === b[j - 1].toLowerCase() ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}
function matchProduct(p: Product, q: string): boolean {
  const query = q.toLowerCase();
  return p.name.toLowerCase().includes(query) || p.slug.toLowerCase().includes(query) || p.category.toLowerCase().includes(query) || p.tagline.toLowerCase().includes(query) || p.goals.some(g => g.toLowerCase().includes(query));
}
function closestMatch(query: string): Product | null {
  if (query.length < 2) return null;
  const ranked = PRODUCTS.map(p => ({
    product: p,
    score: distance(query, p.name)
  })).sort((a, b) => a.score - b.score);
  const best = ranked[0];
  if (!best) return null;
  if (best.score <= Math.max(3, Math.floor(query.length * 0.5))) {
    return best.product;
  }
  return null;
}
const POPULAR = ['BPC-157', 'Semaglutide', 'Tirzepatide', 'TB-500'];
export function SearchPopover({
  open,
  onClose,
  triggerRef
}: Props) {
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => {
    if (!query.trim()) return [];
    return PRODUCTS.filter(p => matchProduct(p, query.trim())).slice(0, 5);
  }, [query]);
  const didYouMean = useMemo(() => {
    if (query.trim() && results.length === 0) {
      return closestMatch(query.trim());
    }
    return null;
  }, [query, results]);
  // Reset + focus on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [open]);
  // Click outside closes — but ignore clicks on the trigger button so it can toggle
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insidePopover = containerRef.current?.contains(target);
      const onTrigger = triggerRef?.current?.contains(target);
      if (!insidePopover && !onTrigger) {
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, triggerRef]);
  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx(i => Math.min(results.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx(i => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results.length, onClose]);
  useEffect(() => {
    setHighlightIdx(0);
  }, [query]);
  return <AnimatePresence data-id="element-700">
      {open && <motion.div ref={containerRef} initial={{
      opacity: 0,
      y: -6,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -6,
      scale: 0.98
    }} transition={{
      duration: 0.18,
      ease: [0.22, 1, 0.36, 1]
    }} className="absolute top-full right-0 mt-2 w-[380px] sm:w-[420px] z-50" role="dialog" aria-label="Search peptides" data-id="element-701">
          <div className="rounded-xl border border-border bg-background shadow-[0_18px_40px_-12px_rgba(15,20,16,0.30),0_4px_12px_-4px_rgba(15,20,16,0.12)] overflow-hidden" data-id="element-702">
            {/* Input row */}
            <div className="flex items-center gap-2.5 h-11 px-3.5 border-b border-border" data-id="element-703">
              <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" strokeWidth={1.75} data-id="element-704" />
              <input ref={inputRef} type="search" placeholder="Search peptides…" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && results[highlightIdx]) {
              e.preventDefault();
              const slug = results[highlightIdx].slug;
              onClose();
              setTimeout(() => {
                window.location.href = `/shop/${slug}`;
              }, 0);
            }
          }} className="flex-1 bg-transparent text-[13.5px] text-foreground placeholder:text-muted-foreground focus:outline-none" data-id="element-705" />
              {query && <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Clear" data-id="element-706">
                  <X className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-707" />
                </button>}
              <kbd className="hidden sm:inline-flex items-center justify-center h-5 px-1.5 rounded-[4px] bg-card border border-border font-mono text-[10px] text-muted-foreground" data-id="element-708">
                Esc
              </kbd>
            </div>

            {/* Content area */}
            <div className="max-h-[380px] overflow-y-auto" data-id="element-709">
              {/* Empty state — popular quick chips */}
              {!query.trim() && <div className="px-3.5 py-3" data-id="element-710">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2.5" data-id="element-711">
                    Popular
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1" data-id="element-712">
                    {POPULAR.map(t => <button key={t} onClick={() => {
                setQuery(t);
                inputRef.current?.focus();
              }} className="inline-flex items-center px-2.5 h-7 rounded-full text-[12px] font-medium bg-secondary/70 text-foreground hover:bg-secondary transition-colors" data-id="element-713">
                        {t}
                      </button>)}
                  </div>
                </div>}

              {/* Results */}
              {results.length > 0 && <ul role="listbox" data-id="element-714">
                  {results.map((p, i) => {
              const active = i === highlightIdx;
              return <li key={p.id} role="option" aria-selected={active} data-id="element-715">
                        <Link to={`/shop/${p.slug}`} onClick={onClose} onMouseEnter={() => setHighlightIdx(i)} className={`flex items-center gap-3 px-3.5 py-2.5 border-b border-border last:border-0 transition-colors ${active ? 'bg-secondary/50' : ''}`} data-id="element-716">
                          <div className="flex-1 min-w-0" data-id="element-717">
                            <div className="flex items-center gap-2" data-id="element-718">
                              <span className="text-[13.5px] font-semibold tracking-tight text-foreground truncate" data-id="element-719">
                                {p.name}
                              </span>
                              <span className="text-[9.5px] uppercase tracking-wider font-medium text-muted-foreground" data-id="element-720">
                                {p.category}
                              </span>
                            </div>
                            <div className="font-mono text-[10.5px] text-muted-foreground mt-0.5 [font-feature-settings:'tnum']" data-id="element-721">
                              <span className="text-foreground" data-id="element-722">
                                {p.doseMg}
                              </span>
                              <span className="text-primary font-semibold" data-id="element-723">
                                mg
                              </span>
                              <span className="text-border mx-1" data-id="element-724">·</span>
                              <span className="text-primary font-semibold" data-id="element-725">
                                {p.purity.toFixed(1)}%
                              </span>{' '}
                              HPLC
                            </div>
                          </div>
                          <span className="font-mono text-[13px] font-medium text-foreground flex-shrink-0 [font-feature-settings:'tnum']" data-id="element-726">
                            £{p.price}
                          </span>
                          <CornerDownLeft className={`h-3 w-3 flex-shrink-0 transition-opacity ${active ? 'text-primary opacity-100' : 'opacity-0'}`} strokeWidth={1.75} data-id="element-727" />
                        </Link>
                      </li>;
            })}
                </ul>}

              {/* Did you mean / no results */}
              {query.trim() && results.length === 0 && <div className="px-3.5 py-5 text-center" data-id="element-728">
                  <p className="text-[12.5px] text-foreground font-medium" data-id="element-729">
                    No peptides matched &ldquo;{query}&rdquo;
                  </p>
                  {didYouMean ? <div className="mt-3" data-id="element-730">
                      <p className="text-[11px] text-muted-foreground mb-2" data-id="element-731">
                        Did you mean…
                      </p>
                      <Link to={`/shop/${didYouMean.slug}`} onClick={onClose} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-primary/10 text-primary border border-primary/20 text-[12.5px] font-semibold hover:bg-primary/15 transition-colors" data-id="element-732">
                        <Search className="h-3 w-3" strokeWidth={2} data-id="element-733" />
                        {didYouMean.name}
                        <ArrowRight className="h-3 w-3" strokeWidth={1.75} data-id="element-734" />
                      </Link>
                    </div> : <p className="text-[11px] text-muted-foreground mt-2" data-id="element-735">
                      Try a different spelling, or{' '}
                      <Link to="/shop" onClick={onClose} className="text-foreground underline underline-offset-2 hover:text-primary" data-id="element-736">
                        browse the catalogue
                      </Link>
                      .
                    </p>}
                </div>}
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}